import { Suspense, lazy } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import type * as SettingsModule from '@/features/settings/pages';
import { Spinner } from '@/components/Misc';
import { LoginPage } from '@/features/auth/LoginPage';
import { MagicLinkPage, ResetPasswordPage, VerifyEmailPage } from '@/features/auth/TokenPages';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { RootRedirect } from '@/features/workspace/RootRedirect';
import { CreateWorkspacePage } from '@/features/workspace/CreateWorkspacePage';
import { WorkspaceIndex, WorkspaceLayout } from '@/features/workspace/WorkspaceLayout';
import { NotFoundPage } from '@/features/workspace/NotFoundPage';
import { LibraryPage } from '@/features/boards/LibraryPage';
import { BoardPage } from '@/features/boards/BoardPage';

const AssetOverlay = lazy(() => import('@/features/asset-detail/AssetOverlay').then((m) => ({ default: m.AssetOverlay })));
const SearchPage = lazy(() => import('@/features/search/SearchPage').then((m) => ({ default: m.SearchPage })));
const PeoplePage = lazy(() => import('@/features/people/PeoplePage').then((m) => ({ default: m.PeoplePage })));
const DuplicatesPage = lazy(() => import('@/features/duplicates/DuplicatesPage').then((m) => ({ default: m.DuplicatesPage })));
const TrashPage = lazy(() => import('@/features/trash').then((m) => ({ default: m.TrashPage }))); // release1/B
const SettingsLayout = lazy(() => import('@/features/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })));
const SettingsIndex = lazy(() => import('@/features/settings/SettingsLayout').then((m) => ({ default: m.SettingsIndex })));
const Settings = lazy(() => import('@/features/settings/pages').then((m) => ({ default: SettingsRoutes(m) })));
const PerfPage = lazy(() => import('@/features/dev/PerfPage').then((m) => ({ default: m.PerfPage })));
const SharePage = lazy(() => import('@/features/share/SharePages').then((m) => ({ default: m.SharePage })));
const ShareAssetPage = lazy(() => import('@/features/share/SharePages').then((m) => ({ default: m.ShareAssetPage })));
const GuestUploadPage = lazy(() => import('@/features/upload/GuestUploadPage').then((m) => ({ default: m.GuestUploadPage })));
// --- release2/A ---
const SystemBoardPage = lazy(() => import('@/features/smart-boards/SystemBoardPage').then((m) => ({ default: m.SystemBoardPage })));

function SettingsRoutes(m: typeof SettingsModule) {
  return function SettingsPage({ page }: { page: string }) {
    switch (page) {
      case 'general': return <m.GeneralSettings />;
      case 'members': return <m.MembersSettings />;
      case 'roles': return <m.RolesSettings />;
      case 'fields': return <m.FieldsSettings />;
      case 'tags': return <m.TagsSettings />;
      case 'share-links': return <m.ShareLinksSettings />;
      case 'audit': return <m.AuditSettings />;
      case 'storage': return <m.StorageSettings />;
      case 'tokens': return <m.TokensSettings />;
      case 'rights': return <m.RightsSettings />; // release2/C
      case 'presets': return <m.ExportPresetsSettings />; // release3/C
      case 'notifications': return <m.NotificationSettings />; // release3/A
      default: return <NotFoundPage />;
    }
  };
}

const Fallback = () => (
  <div className="flex h-full items-center justify-center">
    <Spinner size={20} />
  </div>
);

/**
 * §1.1 route table. The asset overlay (`/w/:ws/a/:assetId`) renders on top of `location.state.background`
 * (the board/search page the user came from) so the grid keeps its scroll position; with no background
 * (deep link) the overlay rewrites history to sit over the asset's home board.
 */
function AppRoutes() {
  const location = useLocation();
  const bg = (location.state as { background?: string } | null)?.background;
  const background: Location | undefined = bg ? ({ ...location, pathname: bg.split('?')[0] ?? bg, search: bg.includes('?') ? bg.slice(bg.indexOf('?')) : '', state: null } as Location) : undefined;
  const isOverlay = /^\/w\/[^/]+\/a\/[^/]+/.test(location.pathname) || /^\/s\/[^/]+\/a\/[^/]+/.test(location.pathname);
  return (
    <Suspense fallback={<Fallback />}>
      <Routes location={background && isOverlay ? background : location}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage mode="signup" />} />
        <Route path="/magic/:token" element={<MagicLinkPage />} />
        <Route path="/verify/:token" element={<VerifyEmailPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage />} />
        <Route path="/s/:token" element={<SharePage />} />
        <Route path="/s/:token/a/:assetId" element={<ShareAssetPage />} />
        <Route path="/u/:token" element={<GuestUploadPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/onboarding" element={<CreateWorkspacePage />} />
          <Route path="/w/:ws" element={<WorkspaceLayout />}>
            <Route index element={<WorkspaceIndex />} />
            <Route path="l/:libraryId" element={<LibraryPage />} />
            <Route path="b/:boardId" element={<BoardPage />} />
            {/* deep link without background: the overlay element below handles it; keep the board slot mounted */}
            <Route path="a/:assetId" element={<WorkspaceIndexForOverlay />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="people" element={<PeoplePage />} />
            <Route path="duplicates" element={<DuplicatesPage />} />
            <Route path="trash" element={<TrashPage />} /> {/* release1/B */}
            <Route path="system/:key" element={<SystemBoardPage />} /> {/* release2/A */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<SettingsIndex />} />
              <Route path=":page" element={<SettingsPageRoute />} />
            </Route>
            <Route path="dev/perf" element={<PerfPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {isOverlay && (
        <Routes>
          <Route element={<RequireAuth />}>
            <Route path="/w/:ws" element={<OverlayHost />}>
              <Route path="a/:assetId" element={<AssetOverlay />} />
            </Route>
          </Route>
          <Route path="/s/:token/a/:assetId" element={background ? <ShareAssetPage /> : null} />
          <Route path="*" element={null} />
        </Routes>
      )}
    </Suspense>
  );
}

/** Placeholder rendered in the primary tree while a deep-linked overlay has no background yet. */
function WorkspaceIndexForOverlay() {
  return <div className="h-full" aria-hidden />;
}

function SettingsPageRoute() {
  const { page = '' } = useParams();
  return <Settings page={page} />;
}

/** Host for the overlay route (the workspace session was already set by WorkspaceLayout in the primary tree). */
function OverlayHost() {
  return <Outlet />;
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
